import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Loader2 } from 'lucide-react';
import { BACKEND_URL } from '../config';

// FitPicker — three-tile fit selector (Classic / Savile Row / Slim).
//
// Oct 2026 (user request — "fit previews background should be grey like the
// official renders. Remove the render clients face onto the mannequin
// button") — the per-client Gemini render pipeline and its manual trigger
// were removed. The picker now simply shows the shared studio-grey mannequin
// references (cached on the backend, matted onto a clean #E5E5E5 backdrop),
// fetched once on mount from GET /api/fit-preview/mannequin/{fit}.
//
// Props:
//   options:      [{ value, label, blurb }] — the three fits
//   value:        currently-selected fit value
//   onChange:     (fitValue) => void
//   accent:       hex string for the active-tile accent colour
//   testidPrefix: used to key data-testids for testing agents
//   disabled:     disable tile selection
export default function FitPicker({
  options,
  value,
  onChange,
  accent = '#C2A878',
  testidPrefix = 'fit-picker',
  disabled = false,
}) {
  const [mannequinRefs, setMannequinRefs] = useState({});

  useEffect(() => {
    let cancelled = false;
    Promise.all(
      options.map((opt) =>
        axios
          .get(`${BACKEND_URL}/api/fit-preview/mannequin/${opt.value}`, {
            withCredentials: true,
            timeout: 90_000,
          })
          .then((r) => ({ fit: opt.value, img: r.data?.image_b64 || null }))
          .catch(() => ({ fit: opt.value, img: null })),
      ),
    ).then((results) => {
      if (cancelled) return;
      const map = {};
      results.forEach((r) => { if (r.img) map[r.fit] = r.img; });
      setMannequinRefs(map);
    });
    return () => { cancelled = true; };
    // options is a stable module-scope constant for every caller.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div data-testid={testidPrefix}>
      <div className="grid grid-cols-3 gap-2">
        {options.map((opt) => {
          const active = value === opt.value;
          const rendered = mannequinRefs[opt.value] || null;
          return (
            <button
              key={opt.value}
              type="button"
              disabled={disabled}
              onClick={() => onChange?.(opt.value)}
              className="text-left border transition-colors overflow-hidden bg-[#E5E5E5] disabled:opacity-50"
              style={{ borderColor: active ? accent : '#27272A' }}
              data-testid={`${testidPrefix}-tile-${opt.value}`}
            >
              <div className="aspect-[3/4] bg-[#E5E5E5] relative overflow-hidden">
                {rendered ? (
                  <img
                    src={`data:image/jpeg;base64,${rendered}`}
                    alt={`${opt.label} fit reference`}
                    className="absolute inset-0 w-full h-full object-contain block"
                    style={{ objectPosition: 'center' }}
                    loading="lazy"
                  />
                ) : (
                  <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 text-[10px] uppercase tracking-widest text-[#71717A]">
                    <Loader2 className="w-4 h-4 animate-spin" style={{ color: accent }} />
                    Loading reference…
                  </div>
                )}
              </div>
              <div className="p-2">
                <div className="text-[11px] uppercase tracking-widest text-[#C2A878]">
                  {opt.label}
                </div>
                <div className="text-[9px] mt-1 leading-tight line-clamp-2" style={{ color: 'rgba(194, 168, 120, 0.65)' }}>
                  {opt.blurb}
                </div>
              </div>
            </button>
          );
        })}
      </div>
      <p className="mt-2 text-[10px] uppercase tracking-widest text-[#52525B]" data-testid={`${testidPrefix}-reference-note`}>
        Reference fits on a studio mannequin
      </p>
    </div>
  );
}
